import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import QuickTransactionActions from '../../components/dashboard/QuickTransactionActions';
import { useDraftAutosave } from '../../hooks/useDraftAutosave';
import { DraftRestoreBanner, DraftSavedIndicator } from '../../components/DraftBanner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { 
  CircleNotch,
  Plus,
  Trash,
  Eye,
  PencilSimple,
  Check,
  X,
  Receipt,
  Money,
  Bank,
  CreditCard,
  FileText,
  ArrowUp,
  ArrowDown,
  MagnifyingGlass,
  Printer,
  Warning,
  Link as LinkIcon
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import RowActionsMenu from '../../components/common/RowActionsMenu';
import DocSummary from '../../components/DocSummary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  pending: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  approved: 'bg-green-500/20 text-green-400 border-green-500/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash', icon: Money },
  { value: 'cheque', label: 'Cheque', icon: FileText },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: Bank },
  { value: 'credit_card', label: 'Credit Card', icon: CreditCard },
  { value: 'other', label: 'Other', icon: Receipt }
];

const formatCurrency = (amount, currency = 'AED') => {
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

export default function VouchersPage() {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('payment');
  const [loading, setLoading] = useState(true);
  
  // Voucher lists
  const [paymentVouchers, setPaymentVouchers] = useState([]);
  const [receiptVouchers, setReceiptVouchers] = useState([]);
  
  // Accounts list for selection
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  // Unpaid / partial invoices for the selected customer (Receipt Voucher only)
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [loadingCustomerInvoices, setLoadingCustomerInvoices] = useState(false);
  
  // Modal states
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [voucherType, setVoucherType] = useState('payment');
  
  // Search & Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editVoucherId, setEditVoucherId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    payee_name: '',
    received_from: '',
    payer_id: '',  // Customer contact id (Receipt Voucher only)
    linked_invoice_id: '',  // Selected invoice to apply this receipt against
    payment_method: 'cash',
    transaction_type: 'expense',
    cheque_number: '',
    cheque_date: '',
    bank_account_id: '',
    cash_account_id: '',
    reference: '',
    manual_voucher_reference: '',
    narration: '',
    linked_sca_id: '',
    sca_payment_type: '',
    lines: [{ account_id: '', description: '', amount: 0 }]
  });

  // SCA dropdown source (loaded once when the modal opens for a payment voucher)
  const [scaOptions, setScaOptions] = useState([]);

  // Draft auto-save (only when creating, not editing)
  const voucherDraft = useDraftAutosave({
    key: `voucher-${formData.transaction_type || 'all'}`,
    data: formData,
    enabled: showVoucherModal && !editMode,
    userId: user?.id,
  });

  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Fetch data
  const fetchVouchers = async () => {
    try {
      const [pvRes, rvRes] = await Promise.all([
        axios.get(`${API_URL}/api/vouchers/payment`, { headers }),
        axios.get(`${API_URL}/api/vouchers/receipt`, { headers })
      ]);
      setPaymentVouchers(pvRes.data || []);
      setReceiptVouchers(rvRes.data || []);
    } catch (err) {
      console.error('Failed to fetch vouchers:', err);
      toast.error('Failed to load vouchers');
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accounts`, { headers });
      setAccounts(res.data || []);
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  };

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/contacts`, { headers });
      setContacts(res.data || []);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    }
  };

  // For receipt vouchers: fetch the customer's unpaid / partial invoices so the
  // user can apply this receipt against a specific invoice. Backend's
  // `_settle_invoice_from_voucher` will adjust amount_paid + status on approval.
  const fetchCustomerOpenInvoices = async (customerId) => {
    if (!customerId) {
      setCustomerInvoices([]);
      return;
    }
    setLoadingCustomerInvoices(true);
    try {
      const res = await axios.get(`${API_URL}/api/invoices?customer_id=${customerId}`, { headers });
      const all = res.data || [];
      // Only show approved/partial invoices with a non-zero balance still due.
      const open = all.filter(inv => {
        const status = (inv.status || '').toLowerCase();
        if (!['approved', 'partial'].includes(status)) return false;
        if (inv.is_deleted) return false;
        const total = Number(inv.total || inv.final_payable || 0);
        const paid = Number(inv.amount_paid || 0);
        return (total - paid) > 0.01;
      }).sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      setCustomerInvoices(open);
    } catch (err) {
      console.error('Failed to fetch customer invoices:', err);
      setCustomerInvoices([]);
    } finally {
      setLoadingCustomerInvoices(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchVouchers(), fetchAccounts(), fetchContacts()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Asset accounts that are clearly NOT cash/bank (so we hide them from dropdowns)
  const NON_CASH_BANK_KEYWORDS = ['receivable', 'inventory', 'stock', 'prepaid', 'fixed asset', 'depreciation', 'goodwill', 'investment', 'deposit (security)', 'advance to'];
  const isNonCashBank = (a) => {
    const n = (a.name || '').toLowerCase();
    return NON_CASH_BANK_KEYWORDS.some(k => n.includes(k));
  };
  const isCashAccount = (a) => {
    if (a.account_type !== 'asset') return false;
    const sub = (a.sub_type || a.account_subtype || '').toLowerCase();
    if (sub === 'cash' || a.is_cash_account === true) return true;
    const n = (a.name || '').toLowerCase();
    if (n.includes('cash') || n.includes('petty') || n.includes('till')) return true;
    if (a.code === '1000' || a.code === '1001') return true;
    return false;
  };
  const isBankAccount = (a) => {
    if (a.account_type !== 'asset') return false;
    const sub = (a.sub_type || a.account_subtype || '').toLowerCase();
    if (sub === 'bank' || a.is_bank_account === true) return true;
    const n = (a.name || '').toLowerCase();
    const bankKeywords = ['bank', 'current account', 'savings', 'checking', 'wio', 'emirates nbd', 'fab', 'mashreq', 'adcb', 'enbd', 'hdfc', 'icici', 'sbi', 'axis', 'kotak', 'rakbank', 'cbd', 'dib', 'rak ', 'al hilal', 'wallet'];
    if (bankKeywords.some(k => n.includes(k))) return true;
    // Code-range heuristic — anything 1001-1099 that isn't cash
    if (a.code && /^10[0-9]{2}$/.test(a.code) && a.code !== '1000') return true;
    return false;
  };
  // Returns the list to show in the dropdown — strict matches first, then any other
  // asset accounts that aren't obviously non-cash/bank, so users with custom-named
  // bank accounts (e.g. "Emirates NBD AED") never see an empty dropdown.
  const cashAccountOptions = () => {
    const matches = accounts.filter(isCashAccount);
    const fallback = accounts.filter(a => a.account_type === 'asset' && !isCashAccount(a) && !isBankAccount(a) && !isNonCashBank(a));
    return { matches, fallback };
  };
  const bankAccountOptions = () => {
    const matches = accounts.filter(isBankAccount);
    const fallback = accounts.filter(a => a.account_type === 'asset' && !isBankAccount(a) && !isCashAccount(a) && !isNonCashBank(a));
    return { matches, fallback };
  };

  // Auto-default cash/bank account when the modal opens, the payment method changes,
  // or accounts finish loading. Without this, vouchers were silently created with
  // empty cash_account_id and would fail on approval (VOUCHER_NO_CASH_BANK).
  useEffect(() => {
    if (!showVoucherModal || !accounts.length) return;
    const isCash = formData.payment_method === 'cash';
    const findCash = () => accounts.find(isCashAccount) || accounts.find(a => a.account_type === 'asset' && !isNonCashBank(a));
    const findBank = () => accounts.find(isBankAccount) || accounts.find(a => a.account_type === 'asset' && !isCashAccount(a) && !isNonCashBank(a));
    setFormData(prev => {
      if (isCash) {
        if (prev.cash_account_id) return prev;
        const def = findCash();
        return def ? { ...prev, cash_account_id: def.id, bank_account_id: '' } : prev;
      }
      if (prev.bank_account_id) return prev;
      const def = findBank();
      return def ? { ...prev, bank_account_id: def.id, cash_account_id: '' } : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showVoucherModal, formData.payment_method, accounts]);

  // Load the eligible SCA list once the voucher modal opens for a payment
  // voucher. Only "approved+" agreements are returned by the endpoint —
  // users cannot book an advance against a draft/rejected contract.
  useEffect(() => {
    if (!showVoucherModal) return;
    if (voucherType !== 'payment') { setScaOptions([]); return; }
    (async () => {
      try {
        const r = await axios.get(
          `${API_URL}/api/subcontractor-agreements/for-payment`,
          { headers },
        );
        setScaOptions(Array.isArray(r.data) ? r.data : []);
      } catch {
        setScaOptions([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showVoucherModal, voucherType]);


  // Reset form
  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      payee_name: '',
      received_from: '',
      payer_id: '',
      linked_invoice_id: '',
      payment_method: 'cash',
      cheque_number: '',
      cheque_date: '',
      bank_account_id: '',
      cash_account_id: '',
      reference: '',
      manual_voucher_reference: '',
      narration: '',
      transaction_type: 'expense',
      linked_sca_id: '',
      sca_payment_type: '',
      lines: [{ account_id: '', description: '', amount: 0 }]
    });
    setCustomerInvoices([]);
  };

  // Auto-open form based on URL ?action=expense|income|transfer
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-apply ?needs_correction=1 → switch to Draft filter and surface a hint.
  // Used by the Dashboard banner to deep-link the user straight at the vouchers
  // that the reconciliation pass reset to draft.
  const needsCorrectionMode = searchParams.get('needs_correction') === '1';
  useEffect(() => {
    if (needsCorrectionMode) {
      setStatusFilter('draft');
    }
  }, [needsCorrectionMode]);

  useEffect(() => {
    const action = searchParams.get('action');
    if (!action) return;
    const map = {
      expense: { vtype: 'payment', tx: 'expense' },
      income: { vtype: 'receipt', tx: 'income' },
      transfer: { vtype: 'payment', tx: 'transfer' },
      refund_paid: { vtype: 'payment', tx: 'refund_paid' },
      refund_received: { vtype: 'receipt', tx: 'refund_received' },
      capital_in: { vtype: 'receipt', tx: 'capital_in' },
      capital_out: { vtype: 'payment', tx: 'capital_out' },
    };
    const cfg = map[action];
    if (cfg) {
      setVoucherType(cfg.vtype);
      setEditMode(false);
      setEditVoucherId(null);

      // Pre-pick a sensible default line account so quick-entry never starts with an empty Account dropdown.
      // Precedence: explicit type match → fallback to any account of the expected family.
      const defaultLineAcctFor = (tx) => {
        const pick = (codeOrName, type) =>
          accounts.find(a => a.account_type === type && (a.code === codeOrName || (a.name || '').toLowerCase().includes(codeOrName.toLowerCase())))
          || accounts.find(a => a.account_type === type);
        switch (tx) {
          case 'expense': return pick('Misc', 'expense') || pick('Office', 'expense') || pick('5400', 'expense');
          case 'income': return pick('Sales', 'income') || pick('Service', 'income') || pick('Other', 'income');
          case 'transfer': return pick('Bank', 'asset') || pick('Cash', 'asset');
          case 'refund_paid': return pick('Sales', 'income') || pick('Other', 'income');
          case 'refund_received': return pick('Misc', 'expense') || pick('Office', 'expense');
          case 'capital_in': return pick('Capital', 'equity') || pick('Owner', 'equity');
          case 'capital_out': return pick('Capital', 'equity') || pick('Owner', 'equity');
          default: return null;
        }
      };
      const defaultLine = defaultLineAcctFor(cfg.tx);

      setFormData(prev => ({
        ...prev,
        date: new Date().toISOString().split('T')[0],
        payee_name: '',
        received_from: '',
        payer_id: '',
        linked_invoice_id: '',
        payment_method: 'cash',
        transaction_type: cfg.tx,
        cheque_number: '',
        cheque_date: '',
        bank_account_id: '',
        cash_account_id: '',
        reference: '',
        manual_voucher_reference: '',
        narration: '',
        lines: [{
          account_id: defaultLine ? defaultLine.id : '',
          description: '',
          amount: 0,
        }]
      }));
      setCustomerInvoices([]);
      setShowVoucherModal(true);
      // Clear the param so reopening doesn't auto-trigger again
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Line item management
  const addLine = () => {
    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, { account_id: '', description: '', amount: 0 }]
    }));
  };

  const updateLine = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.map((line, i) => 
        i === index ? { ...line, [field]: value } : line
      )
    }));
  };

  const removeLine = (index) => {
    if (formData.lines.length === 1) return;
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== index)
    }));
  };

  // Calculate total
  const calculateTotal = () => {
    return formData.lines.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0);
  };

  // Create / Update voucher
  const handleCreateVoucher = async (e) => {
    e.preventDefault();
    
    // Friendly pre-validation so the user sees a clear pointer instead of "Line 1 missing"
    if (!formData.lines || formData.lines.length === 0) {
      toast.error('Add at least one line item with an account and amount before saving.');
      return;
    }
    for (let i = 0; i < formData.lines.length; i++) {
      const ln = formData.lines[i];
      if (!ln.account_id) {
        toast.error(`Line ${i + 1}: please pick an account from the dropdown.`);
        return;
      }
      if (!ln.amount || parseFloat(ln.amount) <= 0) {
        toast.error(`Line ${i + 1}: amount must be greater than 0.`);
        return;
      }
    }
    const isCash = formData.payment_method === 'cash';
    if (isCash && !formData.cash_account_id) {
      toast.error('Please pick a Cash account (the till that holds the money).');
      return;
    }
    if (!isCash && !formData.bank_account_id) {
      toast.error('Please pick a Bank account before saving this voucher.');
      return;
    }

    try {
      const basePath = voucherType === 'payment'
        ? `${API_URL}/api/vouchers/payment`
        : `${API_URL}/api/vouchers/receipt`;

      let res;
      if (editMode && editVoucherId) {
        res = await axios.patch(`${basePath}/${editVoucherId}`, formData, { headers });
        const reverted = res?.data?.reverted_to_draft;
        toast.success(reverted ? 'Voucher updated and reverted to draft. Submit it again for approval.' : 'Voucher updated');
      } else {
        await axios.post(basePath, formData, { headers });
        toast.success(`${voucherType === 'payment' ? 'Payment' : 'Receipt'} voucher created`);
      }
      setShowVoucherModal(false);
      setEditMode(false);
      setEditVoucherId(null);
      if (!editMode) voucherDraft.clear();
      resetForm();
      fetchVouchers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || err.response?.data?.message || err.message || 'Failed to save voucher');
      toast.error(msg, { duration: 6000 });
    }
  };

  // Submit for approval
  const handleSubmitForApproval = async (voucher) => {
    try {
      const endpoint = voucher.voucher_type === 'payment'
        ? `${API_URL}/api/vouchers/payment/${voucher.id}/submit`
        : `${API_URL}/api/vouchers/receipt/${voucher.id}/submit`;
      
      await axios.post(endpoint, {}, { headers });
      toast.success('Voucher submitted for approval');
      fetchVouchers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit voucher');
    }
  };

  // Approve voucher (handles 409 overdraft warning)
  const handleApprove = async (voucher, action, force = false) => {
    try {
      const endpoint = voucher.voucher_type === 'payment'
        ? `${API_URL}/api/vouchers/payment/${voucher.id}/approve`
        : `${API_URL}/api/vouchers/receipt/${voucher.id}/approve`;
      const url = force ? `${endpoint}?force=true` : endpoint;
      await axios.post(url, { action }, { headers });
      toast.success(`Voucher ${action === 'approve' ? 'approved' : 'rejected'}`);
      fetchVouchers();
      setShowViewModal(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === 'INSUFFICIENT_BALANCE') {
        const ok = window.confirm(
          `⚠️ Insufficient Balance\n\n` +
          `Account: ${detail.account_name}\n` +
          `Current balance: ${detail.current_balance.toFixed(2)}\n` +
          `Requested: ${detail.requested_amount.toFixed(2)}\n` +
          `Projected balance: ${detail.projected_balance.toFixed(2)} (NEGATIVE)\n\n` +
          `Approving will leave the account in overdraft. Continue anyway?`
        );
        if (ok) return handleApprove(voucher, action, true);
      } else {
        toast.error(detail?.message || detail || 'Failed to process approval');
      }
    }
  };

  // Heal: backfill missing journal entries for already-approved vouchers
  const handleHealJEs = async () => {
    if (!window.confirm(
      'This will scan all APPROVED vouchers and create missing Journal Entries for any that are missing one (e.g., vouchers approved before the cascade fix).\n\nIt is safe and idempotent — vouchers already linked to a JE will be skipped.\n\nProceed?'
    )) return;
    try {
      const res = await axios.post(`${API_URL}/api/vouchers/heal/backfill-journal-entries`, {}, { headers });
      const t = res.data.total_healed || 0;
      toast.success(t > 0 ? `Healed ${t} voucher(s) — ledgers updated` : 'All vouchers already had Journal Entries');
      fetchVouchers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to heal vouchers');
    }
  };

  // Amend approved voucher: voids JE & creates new draft clone
  const handleAmend = async (voucher) => {
    if (!window.confirm(
      `Amend voucher ${voucher.voucher_number}?\n\n` +
      `This will VOID the current Journal Entry and clone the voucher into a new editable Draft. The original is kept as 'Amended' for audit trail.`
    )) return;
    try {
      const type = voucher.voucher_type || (voucher.payee_name ? 'payment' : 'receipt');
      const res = await axios.post(`${API_URL}/api/vouchers/${type}/${voucher.id}/amend`, {}, { headers });
      toast.success(`Amended → new draft ${res.data.new_voucher_number}`);
      fetchVouchers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to amend voucher');
    }
  };

  // ===== Voucher PDF (view in new tab / download) =====
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [pdfPreviewTitle, setPdfPreviewTitle] = useState('');

  // ===== Attach receipt voucher to invoice (link orphan payments) =====
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [attachVoucher, setAttachVoucher] = useState(null);
  const [attachInvoiceQuery, setAttachInvoiceQuery] = useState('');
  const [attachInvoiceMatches, setAttachInvoiceMatches] = useState([]);
  const [attachLoading, setAttachLoading] = useState(false);
  const [attachSubmitting, setAttachSubmitting] = useState(false);

  const openAttachDialog = (voucher) => {
    setAttachVoucher(voucher);
    setAttachInvoiceQuery('');
    setAttachInvoiceMatches([]);
    setAttachDialogOpen(true);
  };

  // Search invoices by document_number / customer_name — used by the
  // Attach-to-Invoice dialog to help the user pick the right one.
  useEffect(() => {
    if (!attachDialogOpen) return;
    let cancelled = false;
    (async () => {
      setAttachLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/invoices`, { headers });
        if (cancelled) return;
        const all = res.data || [];
        const q = (attachInvoiceQuery || '').trim().toLowerCase();
        // Prefer outstanding, then match by number/customer, cap at 25
        const outstanding = all.filter((i) => (
          !i.is_deleted
          && (i.status === 'approved' || i.status === 'partial')
          && (i.invoice_type !== 'proforma')
          && (i.balance_due === undefined || i.balance_due === null || i.balance_due > 0.01)
        ));
        const filtered = q
          ? outstanding.filter((i) => (
              (i.document_number || '').toLowerCase().includes(q)
              || (i.customer_name || '').toLowerCase().includes(q)
            ))
          : outstanding;
        setAttachInvoiceMatches(filtered.slice(0, 25));
      } catch (err) {
        toast.error('Failed to load invoices');
      } finally {
        if (!cancelled) setAttachLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [attachDialogOpen, attachInvoiceQuery]);

  const handleConfirmAttach = async (invoice) => {
    if (!attachVoucher || !invoice) return;
    setAttachSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/vouchers/receipt/${attachVoucher.id}/attach-invoice`,
        { invoice_id: invoice.id },
        { headers },
      );
      const data = res.data || {};
      const parts = [];
      if (data.payer_corrected) parts.push('payer auto-corrected to invoice customer');
      if (data.invoice?.amount_paid !== undefined) parts.push(`invoice paid=${data.invoice.amount_paid}`);
      if (data.invoice?.balance_due !== undefined) parts.push(`balance=${data.invoice.balance_due}`);
      toast.success(`Attached ${attachVoucher.voucher_number} → ${invoice.document_number}` + (parts.length ? ` · ${parts.join(' · ')}` : ''));
      setAttachDialogOpen(false);
      setAttachVoucher(null);
      fetchVouchers();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let msg = 'Failed to attach voucher';
      if (typeof raw === 'string') msg = raw;
      else if (raw && typeof raw === 'object') msg = raw.message || raw.code || JSON.stringify(raw);
      else if (err?.message) msg = err.message;
      toast.error(msg);
    } finally {
      setAttachSubmitting(false);
    }
  };

  const fetchVoucherPdfBlob = async (voucher) => {
    const kind = voucher.voucher_type || (voucher.payee_name ? 'payment' : 'receipt');
    const res = await axios.get(`${API_URL}/api/vouchers/${kind}/${voucher.id}/pdf`, {
      headers,
      responseType: 'blob',
    });
    return new Blob([res.data], { type: 'application/pdf' });
  };

  const handleViewPdf = async (voucher) => {
    try {
      const blob = await fetchVoucherPdfBlob(voucher);
      const url = window.URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
      setPdfPreviewTitle(`${voucher.voucher_type === 'payment' || voucher.payee_name ? 'Payment Voucher' : 'Receipt Voucher'} ${voucher.voucher_number || ''}`);
      setPdfPreviewOpen(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to load voucher PDF');
    }
  };

  const handleDownloadPdf = async (voucher) => {
    try {
      const blob = await fetchVoucherPdfBlob(voucher);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${voucher.voucher_number || voucher.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to download PDF');
    }
  };

  // Edit voucher (drafts and pending — pending will revert to draft on save)
  const handleEdit = (voucher) => {
    if (!['draft', 'pending'].includes(voucher.status)) {
      toast.error('Only draft or pending vouchers can be edited. Use "Amend" for approved vouchers.');
      return;
    }
    if (voucher.status === 'pending') {
      if (!window.confirm(
        `Voucher ${voucher.voucher_number} is pending approval. Editing will revert it to a draft and cancel the current approval request.\n\n` +
        `You will need to submit it for approval again after saving. Continue?`
      )) return;
    }
    setVoucherType(voucher.voucher_type || (voucher.payee_name ? 'payment' : 'receipt'));
    setEditMode(true);
    setEditVoucherId(voucher.id);
    setFormData({
      date: voucher.date || new Date().toISOString().split('T')[0],
      payee_name: voucher.payee_name || '',
      received_from: voucher.received_from || '',
      payer_id: voucher.payer_id || '',
      linked_invoice_id: voucher.linked_invoice_id || '',
      payment_method: voucher.payment_method || 'cash',
      transaction_type: voucher.transaction_type || (voucher.payee_name ? 'expense' : 'income'),
      cheque_number: voucher.cheque_number || '',
      cheque_date: voucher.cheque_date || '',
      bank_account_id: voucher.bank_account_id || '',
      cash_account_id: voucher.cash_account_id || '',
      reference: voucher.reference || '',
      manual_voucher_reference: voucher.manual_voucher_reference || '',
      narration: voucher.narration || '',
      linked_sca_id: voucher.linked_sca_id || '',
      sca_payment_type: voucher.sca_payment_type || '',
      lines: voucher.lines && voucher.lines.length ? voucher.lines : [{ account_id: '', description: '', amount: 0 }],
    });
    // For receipt vouchers in edit mode, reload the customer's open invoices so
    // the dropdown shows the currently-linked invoice as the selected option.
    if ((voucher.voucher_type || (voucher.payee_name ? 'payment' : 'receipt')) === 'receipt' && voucher.payer_id) {
      fetchCustomerOpenInvoices(voucher.payer_id);
    } else {
      setCustomerInvoices([]);
    }
    setShowVoucherModal(true);
  };

  // Delete voucher (with strong confirmation for approved/posted vouchers)
  const handleDelete = async (voucher) => {
    const isLocked = ['approved', 'posted', 'partial', 'partially_paid', 'paid'].includes((voucher.status || '').toLowerCase());
    if (isLocked) {
      const promptText = window.prompt(
        `DANGER — Delete ${voucher.status?.toUpperCase()} voucher ${voucher.voucher_number}?\n\n` +
        `This will:\n` +
        `  • Soft-delete the voucher (recoverable from Recycle Bin)\n` +
        `  • VOID the linked Journal Entry (kept for audit)\n` +
        `  • Remove the amount from Statement of Account / Cash Position\n\n` +
        `Type DELETE to confirm:`
      );
      if ((promptText || '').trim().toUpperCase() !== 'DELETE') {
        if (promptText !== null) toast.info('Delete cancelled.');
        return;
      }
    } else if (!window.confirm(`Delete voucher ${voucher.voucher_number}?`)) {
      return;
    }
    try {
      const type = voucher.voucher_type || (voucher.payee_name ? 'payment' : 'receipt');
      await axios.delete(`${API_URL}/api/vouchers/${type}/${voucher.id}`, { headers });
      toast.success(isLocked ? 'Voucher deleted and JE voided' : 'Voucher deleted');
      fetchVouchers();
    } catch (err) {
      // err.response.data.detail may be a string OR an object {code, message}
      // — passing the object to toast.error crashes sonner and freezes the UI.
      const raw = err?.response?.data?.detail;
      let msg = 'Failed to delete voucher';
      if (typeof raw === 'string') msg = raw;
      else if (raw && typeof raw === 'object') msg = raw.message || raw.code || JSON.stringify(raw);
      else if (err?.message) msg = err.message;
      toast.error(msg);
    }
  };

  // Export vouchers
  const handleExport = async (fmt) => {
    try {
      const params = new URLSearchParams({
        voucher_type: activeTab,
        fmt,
      });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      const res = await axios.get(`${API_URL}/api/vouchers/export?${params.toString()}`, {
        headers,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vouchers_${new Date().toISOString().split('T')[0]}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${fmt.toUpperCase()}`);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  // Open new voucher modal
  const openNewVoucher = (type) => {
    setVoucherType(type);
    setEditMode(false);
    setEditVoucherId(null);
    resetForm();
    setShowVoucherModal(true);
  };

  // View voucher
  const viewVoucher = (voucher) => {
    setSelectedVoucher(voucher);
    setShowViewModal(true);
  };

  // Filter vouchers by search + status + date range
  const filterVouchers = (vouchers) => {
    let out = vouchers;
    if (statusFilter && statusFilter !== 'all') {
      out = out.filter(v => v.status === statusFilter);
    }
    if (fromDate) out = out.filter(v => (v.date || '') >= fromDate);
    if (toDate) out = out.filter(v => (v.date || '') <= toDate);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      out = out.filter(v =>
        v.voucher_number?.toLowerCase().includes(term) ||
        v.payee_name?.toLowerCase().includes(term) ||
        v.received_from?.toLowerCase().includes(term) ||
        v.narration?.toLowerCase().includes(term) ||
        v.manual_voucher_reference?.toLowerCase().includes(term) ||
        v.reference?.toLowerCase().includes(term)
      );
    }
    return out;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <CircleNotch className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Render voucher list
  const renderVoucherList = (vouchers, type) => (
    <div className="space-y-3">
      {filterVouchers(vouchers).length > 0 ? (
        filterVouchers(vouchers).map((voucher) => (
          <motion.div
            key={voucher.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-xl p-4 hover:border-primary/30 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${type === 'payment' ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
                  {type === 'payment' ? 
                    <ArrowUp className="w-6 h-6 text-red-400" weight="bold" /> :
                    <ArrowDown className="w-6 h-6 text-green-400" weight="bold" />
                  }
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold">{voucher.voucher_number}</h3>
                    <Badge variant="outline" className={STATUS_COLORS[voucher.status]}>
                      {voucher.status}
                    </Badge>
                    {voucher.needs_account_correction && (
                      <Badge
                        variant="outline"
                        className="bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40"
                        title={voucher.correction_note || 'Reset by reconciliation — pick the correct expense/income account before re-approving.'}
                        data-testid={`voucher-needs-correction-${voucher.id}`}
                      >
                        Needs account correction
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {type === 'payment' ? voucher.payee_name : voucher.received_from}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(voucher.date)} • {PAYMENT_METHODS.find(m => m.value === voucher.payment_method)?.label || voucher.payment_method}
                    {voucher.cheque_number && ` • Cheque #${voucher.cheque_number}`}
                    {voucher.manual_voucher_reference && ` • Manual Ref: ${voucher.manual_voucher_reference}`}
                    {voucher.linked_sca_reference && ` • ${voucher.linked_sca_reference}${voucher.sca_payment_type ? ' · ' + ({advance:'Advance', progress:'Progress', final:'Final'}[voucher.sca_payment_type] || voucher.sca_payment_type) + ' Payment' : ''}`}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className={`text-xl font-bold ${type === 'payment' ? 'text-red-400' : 'text-green-400'}`}>
                    {formatCurrency(voucher.total_amount)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {voucher.narration?.substring(0, 30)}...
                  </p>
                </div>
                
                <div className="flex gap-1">
                  <RowActionsMenu
                    testid={`voucher-${voucher.id}`}
                    actions={[
                      { id: 'view', label: 'View', icon: Eye, onClick: () => viewVoucher(voucher) },
                      { id: 'pdf', label: 'View / Print PDF', icon: Printer, onClick: () => handleViewPdf(voucher) },
                      { id: 'download', label: 'Download PDF', icon: FileText, onClick: () => handleDownloadPdf(voucher) },
                      voucher.status === 'draft' && 'separator',
                      voucher.status === 'draft' && { id: 'edit', label: 'Edit', icon: PencilSimple, onClick: () => handleEdit(voucher) },
                      voucher.status === 'draft' && { id: 'submit', label: 'Submit for Approval', icon: Check, onClick: () => handleSubmitForApproval(voucher) },
                      voucher.status === 'pending' && { id: 'edit-pending', label: 'Edit (revert to draft)', icon: PencilSimple, onClick: () => handleEdit(voucher) },
                      voucher.status === 'approved' && isAdmin && { id: 'amend', label: 'Amend (void & re-issue)', icon: PencilSimple, onClick: () => handleAmend(voucher) },
                      // "Attach to Invoice" — visible for APPROVED receipt vouchers
                      // that have no linked_invoice_id, OR were linked to a
                      // Proforma / Quotation. Lets the user manually link an
                      // orphan receipt to the correct tax invoice so SoA and
                      // amount_paid update immediately.
                      type === 'receipt'
                        && ['approved', 'posted', 'partial', 'partially_paid', 'paid'].includes(voucher.status)
                        && !voucher.linked_invoice_id
                        && { id: 'attach-invoice', label: 'Attach to Invoice…', icon: LinkIcon, onClick: () => openAttachDialog(voucher) },
                      ((voucher.status === 'draft' || isAdmin)) && 'separator',
                      ((voucher.status === 'draft' || isAdmin)) && { id: 'delete', label: voucher.status === 'amended' ? 'Delete (Amended)' : 'Delete', icon: Trash, onClick: () => handleDelete(voucher), destructive: true },
                    ].filter(Boolean)}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        ))
      ) : (
        <div className="text-center py-12">
          <Receipt className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No {type} vouchers found</p>
          <Button className="mt-4" onClick={() => openNewVoucher(type)}>
            <Plus className="w-4 h-4 mr-2" />
            Create {type === 'payment' ? 'Payment' : 'Receipt'} Voucher
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="vouchers-page">
      {/* Needs-correction guide banner — appears when arriving via the dashboard
          deep-link. Tells the user precisely what to do to clear the issue. */}
      {needsCorrectionMode && (
        <div
          className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4 flex items-start gap-3"
          data-testid="vouchers-needs-correction-hint"
        >
          <Warning size={22} weight="duotone" className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-amber-800 dark:text-amber-200">
              How to clear &ldquo;Needs account correction&rdquo; vouchers
            </p>
            <ol className="list-decimal pl-5 mt-1 space-y-0.5 text-xs text-muted-foreground">
              <li>Open each draft with the amber <span className="font-semibold">Needs account correction</span> badge below.</li>
              <li>In the voucher form, change the <span className="font-semibold">line Account</span> from the bank/cash account to the correct <span className="font-semibold">Expense</span> account (e.g., &ldquo;Fuel Expense &mdash; 5500&rdquo;).</li>
              <li>Save (or Submit), then re-approve. The approval will be blocked until the line is no longer pointing at the bank, so you cannot accidentally re-create the same broken JE.</li>
              <li>Once approved, the amount will appear under P&amp;L &rarr; Expenses for the voucher date.</li>
            </ol>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Receipt className="w-7 h-7 text-primary" weight="duotone" />
            Payment & Receipt Vouchers
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage payment and receipt transactions
          </p>
        </div>
        
        <div className="flex gap-2">
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleHealJEs}
              data-testid="heal-vouchers-btn"
              title="Backfill missing Journal Entries for approved vouchers (idempotent, safe to re-run)"
            >
              <CircleNotch className="w-4 h-4 mr-2" />
              Re-sync Ledger
            </Button>
          )}
          <Button onClick={() => openNewVoucher('payment')} className="bg-red-500 hover:bg-red-600" data-testid="new-payment-voucher-btn">
            <ArrowUp className="w-4 h-4 mr-2" />
            Payment Voucher
          </Button>
          <Button onClick={() => openNewVoucher('receipt')} className="bg-green-500 hover:bg-green-600" data-testid="new-receipt-voucher-btn">
            <ArrowDown className="w-4 h-4 mr-2" />
            Receipt Voucher
          </Button>
        </div>
      </div>

      {/* Quick Action buttons (Expense / Income / Transfer / Adjustment) */}
      <QuickTransactionActions />

      {/* Filters & Export */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
            <div className="relative md:col-span-2">
              <Label className="text-xs">Search</Label>
              <MagnifyingGlass className="absolute left-3 top-[34px] w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Voucher #, name, manual ref…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="voucher-search"
              />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="voucher-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="voucher-from-date" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="voucher-to-date" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleExport('csv')} data-testid="export-csv-btn" title="Export to CSV (Excel)">
                <FileText className="w-4 h-4 mr-1" /> CSV
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn" title="Export to PDF">
                <Printer className="w-4 h-4 mr-1" /> PDF
              </Button>
            </div>
          </div>
          {(searchTerm || statusFilter !== 'all' || fromDate || toDate) && (
            <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
              <span>Filters active</span>
              <Button
                variant="ghost" size="sm"
                onClick={() => { setSearchTerm(''); setStatusFilter('all'); setFromDate(''); setToDate(''); }}
                data-testid="voucher-clear-filters"
                className="h-6 text-xs"
              >
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Payments</p>
                <p className="text-2xl font-bold text-red-400">
                  {formatCurrency(paymentVouchers.filter(v => v.status === 'approved').reduce((sum, v) => sum + (v.total_amount || 0), 0))}
                </p>
              </div>
              <ArrowUp className="w-8 h-8 text-red-400/50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Receipts</p>
                <p className="text-2xl font-bold text-green-400">
                  {formatCurrency(receiptVouchers.filter(v => v.status === 'approved').reduce((sum, v) => sum + (v.total_amount || 0), 0))}
                </p>
              </div>
              <ArrowDown className="w-8 h-8 text-green-400/50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Approval</p>
                <p className="text-2xl font-bold">
                  {[...paymentVouchers, ...receiptVouchers].filter(v => v.status === 'pending').length}
                </p>
              </div>
              <Badge className="bg-blue-500/20 text-blue-400">Pending</Badge>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Draft</p>
                <p className="text-2xl font-bold">
                  {[...paymentVouchers, ...receiptVouchers].filter(v => v.status === 'draft').length}
                </p>
              </div>
              <Badge className="bg-yellow-500/20 text-yellow-400">Draft</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="payment" className="flex items-center gap-2" data-testid="tab-payment">
            <ArrowUp className="w-4 h-4" />
            Payment Vouchers ({paymentVouchers.length})
          </TabsTrigger>
          <TabsTrigger value="receipt" className="flex items-center gap-2" data-testid="tab-receipt">
            <ArrowDown className="w-4 h-4" />
            Receipt Vouchers ({receiptVouchers.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="payment" className="mt-4">
          <DocSummary kind="payment_vouchers" label="Payment Vouchers" filters={{ q: searchTerm }} />
          {renderVoucherList(paymentVouchers, 'payment')}
        </TabsContent>

        <TabsContent value="receipt" className="mt-4">
          <DocSummary kind="receipt_vouchers" label="Receipt Vouchers" filters={{ q: searchTerm }} />
          {renderVoucherList(receiptVouchers, 'receipt')}
        </TabsContent>
      </Tabs>

      {/* Create Voucher Modal */}
      <Dialog open={showVoucherModal} onOpenChange={setShowVoucherModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {voucherType === 'payment' ? 
                <ArrowUp className="w-5 h-5 text-red-400" /> :
                <ArrowDown className="w-5 h-5 text-green-400" />
              }
              {editMode ? 'Edit' : 'New'} {voucherType === 'payment' ? 'Payment' : 'Receipt'} Voucher
            </DialogTitle>
            <DialogDescription>
              {editMode ? 'Update voucher details (only drafts can be edited)' : `Record a ${voucherType === 'payment' ? 'payment made' : 'payment received'}`}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateVoucher} className="space-y-6">
            {!editMode && (
              <DraftRestoreBanner
                visible={voucherDraft.hasDraft}
                savedAt={voucherDraft.draftSavedAt}
                label={`${voucherType === 'payment' ? 'payment' : 'receipt'} voucher`}
                onRestore={() => { const r = voucherDraft.restore(); if (r) setFormData(r); }}
                onDiscard={voucherDraft.discard}
              />
            )}
            <div className="grid grid-cols-2 gap-4">
              {/* Transaction Type — guides user and filters accounts */}
              <div className="col-span-2 bg-primary/5 border border-primary/20 rounded-lg p-3">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Transaction Type</Label>
                <Select
                  value={formData.transaction_type}
                  onValueChange={(v) => {
                    setFormData(prev => ({ ...prev, transaction_type: v }));
                    // Auto-flip voucher type for transactions that imply receipt vs payment
                    if (['income','refund_received','capital_in'].includes(v)) setVoucherType('receipt');
                    else if (['expense','transfer','refund_paid','capital_out'].includes(v)) setVoucherType('payment');
                  }}
                >
                  <SelectTrigger data-testid="voucher-tx-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expense">💸 Expense (cash/bank paid out)</SelectItem>
                    <SelectItem value="income">💰 Income (cash/bank received)</SelectItem>
                    <SelectItem value="transfer">🔁 Transfer (bank ↔ bank, cash ↔ bank)</SelectItem>
                    <SelectItem value="refund_paid">↩️ Refund Paid (income reversal out)</SelectItem>
                    <SelectItem value="refund_received">↪️ Refund Received (expense reversal in)</SelectItem>
                    <SelectItem value="capital_in">📥 Capital / Owner Contribution In</SelectItem>
                    <SelectItem value="capital_out">📤 Capital / Drawing Out</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {{
                    expense: 'Pick an Expense account on the line. Cash/Bank credited.',
                    income: 'Pick an Income account on the line. Cash/Bank debited.',
                    transfer: 'Source = Cash/Bank field below. Destination = Asset account on the line.',
                    refund_paid: 'Reverses an income line — refund issued to a customer.',
                    refund_received: 'Reverses an expense line — refund received from a supplier.',
                    capital_in: 'Owner injects capital — pick an Equity/Liability account on the line.',
                    capital_out: 'Owner draws capital out — pick the Equity (drawing) account on the line.',
                  }[formData.transaction_type] || ''}
                </p>
              </div>

              <div className="col-span-2 space-y-2">
                <Label>{voucherType === 'payment' ? 'Payee Name *' : 'Received From *'}</Label>
                {voucherType === 'receipt' ? (
                  <>
                    {/* Customer dropdown — auto-fills received_from + loads open invoices */}
                    <Select
                      value={formData.payer_id || 'walk-in'}
                      onValueChange={(v) => {
                        if (v === 'walk-in') {
                          setFormData(prev => ({ ...prev, payer_id: '', linked_invoice_id: '' }));
                          setCustomerInvoices([]);
                        } else {
                          const c = contacts.find(x => x.id === v);
                          setFormData(prev => ({
                            ...prev,
                            payer_id: v,
                            received_from: c?.name || c?.company_name || prev.received_from,
                            linked_invoice_id: '',  // reset invoice when customer changes
                          }));
                          fetchCustomerOpenInvoices(v);
                        }
                      }}
                    >
                      <SelectTrigger data-testid="voucher-customer-select">
                        <SelectValue placeholder="Select a customer (or use walk-in)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="walk-in">— Walk-in / Manual entry —</SelectItem>
                        {contacts
                          .filter(c => (c.contact_type || '').toLowerCase() === 'customer' || !c.contact_type)
                          .sort((a, b) => (a.name || a.company_name || '').localeCompare(b.name || b.company_name || ''))
                          .map(c => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name || c.company_name}{c.company_name && c.name ? ` · ${c.company_name}` : ''}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={formData.received_from}
                      onChange={(e) => setFormData(prev => ({ ...prev, received_from: e.target.value }))}
                      placeholder="Payer name (auto-filled from customer)"
                      required
                      data-testid="voucher-party-name"
                    />
                  </>
                ) : (
                  <Input
                    value={formData.payee_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, payee_name: e.target.value }))}
                    placeholder="Enter payee name"
                    required
                    data-testid="voucher-party-name"
                  />
                )}
              </div>

              {/* Invoice allocation — visible only for receipt vouchers with a selected customer */}
              {voucherType === 'receipt' && formData.payer_id && (
                <div className="col-span-2 space-y-2">
                  <Label>Apply Against Invoice (optional)</Label>
                  <Select
                    value={formData.linked_invoice_id || 'none'}
                    onValueChange={(v) => {
                      if (v === 'none') {
                        setFormData(prev => ({ ...prev, linked_invoice_id: '' }));
                        return;
                      }
                      const inv = customerInvoices.find(x => x.id === v);
                      const due = inv ? Math.max(0, Number(inv.total || 0) - Number(inv.amount_paid || 0)) : 0;
                      setFormData(prev => ({
                        ...prev,
                        linked_invoice_id: v,
                        reference: inv?.document_number || prev.reference,
                        narration: prev.narration || (inv ? `Payment received against ${inv.document_number}` : prev.narration),
                        // Pre-fill the first line amount with the balance due so users don't retype it.
                        // We DON'T overwrite if the user has already entered a non-zero amount.
                        lines: prev.lines.map((ln, i) => i === 0 && !ln.amount ? { ...ln, amount: due, description: ln.description || `Receipt for ${inv?.document_number || ''}` } : ln),
                      }));
                    }}
                  >
                    <SelectTrigger data-testid="voucher-invoice-select">
                      <SelectValue placeholder={loadingCustomerInvoices ? 'Loading invoices…' : 'Select an invoice or leave as on-account'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— On-account (no specific invoice) —</SelectItem>
                      {customerInvoices.length === 0 && !loadingCustomerInvoices && (
                        <div className="px-3 py-2 text-xs text-muted-foreground">No open invoices for this customer.</div>
                      )}
                      {customerInvoices.map(inv => {
                        const due = Math.max(0, Number(inv.total || 0) - Number(inv.amount_paid || 0));
                        return (
                          <SelectItem key={inv.id} value={inv.id}>
                            {inv.document_number} · {inv.date} · Due {Number(due).toFixed(2)}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">
                    Linking to an invoice automatically settles it (and flips status to <strong>Partial</strong> or <strong>Paid</strong>) when this voucher is approved.
                  </p>
                </div>
              )}
              
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  required
                  data-testid="voucher-date"
                />
              </div>
              
              <div>
                <Label>Payment Method *</Label>
                <Select
                  value={formData.payment_method}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, payment_method: v }))}
                >
                  <SelectTrigger data-testid="voucher-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(method => (
                      <SelectItem key={method.value} value={method.value}>
                        <div className="flex items-center gap-2">
                          <method.icon className="w-4 h-4" />
                          {method.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Cash / Bank Account selection — REQUIRED for accounting posting */}
              {formData.payment_method === 'cash' ? (
                <div>
                  <Label>Cash Account *</Label>
                  <Select
                    value={formData.cash_account_id}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, cash_account_id: v, bank_account_id: '' }))}
                  >
                    <SelectTrigger data-testid="voucher-cash-account">
                      <SelectValue placeholder="Select cash account" />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const { matches, fallback } = cashAccountOptions();
                        if (matches.length === 0 && fallback.length === 0) {
                          return <div className="px-3 py-2 text-xs text-muted-foreground">No asset accounts found — create one in Chart of Accounts first.</div>;
                        }
                        return (
                          <>
                            {matches.map(a => (
                              <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>
                            ))}
                            {fallback.length > 0 && (
                              <>
                                {matches.length > 0 && <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-muted-foreground border-t mt-1 pt-2">Other asset accounts</div>}
                                {fallback.map(a => (
                                  <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>
                                ))}
                              </>
                            )}
                          </>
                        );
                      })()}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Pick the cash/till account that holds the money. Don't see yours? Add it under Accounts → Chart of Accounts (type = Asset).
                  </p>
                </div>
              ) : (
                <div>
                  <Label>Bank Account *</Label>
                  <Select
                    value={formData.bank_account_id}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, bank_account_id: v, cash_account_id: '' }))}
                  >
                    <SelectTrigger data-testid="voucher-bank-account">
                      <SelectValue placeholder="Select bank account" />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const { matches, fallback } = bankAccountOptions();
                        if (matches.length === 0 && fallback.length === 0) {
                          return <div className="px-3 py-2 text-xs text-muted-foreground">No asset accounts found — create one in Chart of Accounts first.</div>;
                        }
                        return (
                          <>
                            {matches.map(a => (
                              <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>
                            ))}
                            {fallback.length > 0 && (
                              <>
                                {matches.length > 0 && <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-muted-foreground border-t mt-1 pt-2">Other asset accounts</div>}
                                {fallback.map(a => (
                                  <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>
                                ))}
                              </>
                            )}
                          </>
                        );
                      })()}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Pick the bank that will receive/send the money. Don't see yours? Add a bank account in Chart of Accounts (Asset → name it like "Emirates NBD" or set sub-type = Bank).
                  </p>
                </div>
              )}
              
              {formData.payment_method === 'cheque' && (
                <>
                  <div>
                    <Label>Cheque Number</Label>
                    <Input
                      value={formData.cheque_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, cheque_number: e.target.value }))}
                      placeholder="CHQ001"
                      data-testid="voucher-cheque-number"
                    />
                  </div>
                  <div>
                    <Label>Cheque Date</Label>
                    <Input
                      type="date"
                      value={formData.cheque_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, cheque_date: e.target.value }))}
                      data-testid="voucher-cheque-date"
                    />
                  </div>
                </>
              )}
              
              <div>
                <Label>Reference</Label>
                <Input
                  value={formData.reference}
                  onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                  placeholder="Internal reference (optional)"
                  data-testid="voucher-reference"
                />
              </div>

              <div>
                <Label>Manual / External Voucher #</Label>
                <Input
                  value={formData.manual_voucher_reference}
                  onChange={(e) => setFormData(prev => ({ ...prev, manual_voucher_reference: e.target.value }))}
                  placeholder="e.g. PV-2024-001 (paper book)"
                  data-testid="voucher-manual-reference"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  For audit reconciliation with pre-printed/manual vouchers.
                </p>
              </div>
            </div>

            {/* Subcontractor Agreement linkage (payment vouchers only) */}
            {voucherType === 'payment' && (
              <div className="rounded-lg border border-border/60 bg-muted/30 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Subcontractor Agreement <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  {formData.linked_sca_id && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setFormData(prev => ({ ...prev, linked_sca_id: '', sca_payment_type: '' }))}
                      data-testid="voucher-clear-sca">
                      Clear
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Reference</Label>
                    <select
                      className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                      value={formData.linked_sca_id || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, linked_sca_id: e.target.value }))}
                      data-testid="voucher-sca-select"
                    >
                      <option value="">— Select SCA —</option>
                      {scaOptions.map(sca => (
                        <option key={sca.id} value={sca.id}>
                          {sca.reference_number} · {sca.second_party?.name || 'Unknown'} · {sca.title?.slice(0, 40) || ''}
                        </option>
                      ))}
                    </select>
                    {scaOptions.length === 0 && (
                      <p className="text-[11px] text-muted-foreground mt-1">
                        No approved agreements available. Approve an SCA first to link payments.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Payment Type</Label>
                    <select
                      className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm disabled:opacity-50"
                      value={formData.sca_payment_type || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, sca_payment_type: e.target.value }))}
                      disabled={!formData.linked_sca_id}
                      data-testid="voucher-sca-payment-type"
                    >
                      <option value="">—</option>
                      <option value="advance">Advance Payment</option>
                      <option value="progress">Progress Payment</option>
                      <option value="final">Final Payment</option>
                    </select>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Shown in journal entries &amp; account statements.
                    </p>
                  </div>
                </div>
              </div>
            )}



            {/* Line Items */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-base">Line Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLine}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Line
                </Button>
              </div>
              
              <div className="space-y-2">
                {formData.lines.map((line, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 bg-muted/30 rounded-lg">
                    <div className="col-span-4">
                      <Label className="text-xs">Account</Label>
                      <Select
                        value={line.account_id}
                        onValueChange={(v) => updateLine(idx, 'account_id', v)}
                      >
                        <SelectTrigger data-testid={`voucher-line-account-${idx}`}>
                          <SelectValue placeholder="Select account" />
                        </SelectTrigger>
                        <SelectContent>
                          {(() => {
                            const tx = formData.transaction_type;
                            const filterMap = {
                              expense: ['expense', 'asset', 'liability'],
                              income: ['income'],
                              transfer: ['asset'], // destination cash/bank
                              refund_paid: ['income', 'liability'],
                              refund_received: ['expense', 'asset'],
                              capital_in: ['equity', 'liability'],
                              capital_out: ['equity'],
                            };
                            const allowed = filterMap[tx] || null;
                            const filtered = allowed ? accounts.filter(a => allowed.includes(a.account_type)) : accounts;
                            // If filter wipes everything (eg, user has no accounts of that type), fall back to ALL accounts
                            const finalList = filtered.length > 0 ? filtered : accounts;
                            if (finalList.length === 0) {
                              return <div className="px-3 py-2 text-xs text-muted-foreground">No accounts found — create one in Chart of Accounts first.</div>;
                            }
                            return finalList.map(acc => (
                              <SelectItem key={acc.id} value={acc.id}>
                                {acc.code} - {acc.name}
                              </SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-5">
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={line.description}
                        onChange={(e) => updateLine(idx, 'description', e.target.value)}
                        placeholder="Description"
                        data-testid={`voucher-line-desc-${idx}`}
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Amount</Label>
                      <Input
                        type="number"
                        value={line.amount || ''}
                        onChange={(e) => updateLine(idx, 'amount', e.target.value)}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className="text-right font-mono text-sm"
                        data-testid={`voucher-line-amount-${idx}`}
                      />
                    </div>
                    <div className="col-span-1">
                      {formData.lines.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(idx)}>
                          <Trash className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-end">
                <div className="text-right">
                  <span className="text-sm text-muted-foreground">Total: </span>
                  <span className={`text-xl font-bold ${voucherType === 'payment' ? 'text-red-400' : 'text-green-400'}`}>
                    {formatCurrency(calculateTotal())}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <Label>Narration</Label>
              <Textarea
                value={formData.narration}
                onChange={(e) => setFormData(prev => ({ ...prev, narration: e.target.value }))}
                rows={2}
                placeholder="Describe the purpose of this voucher..."
                data-testid="voucher-narration"
              />
            </div>

            <DialogFooter>
              {!editMode && <div className="mr-auto"><DraftSavedIndicator savedAt={voucherDraft.lastAutoSavedAt} /></div>}
              <Button type="button" variant="outline" onClick={() => setShowVoucherModal(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-voucher-form">
                {editMode ? 'Update Voucher' : 'Create Voucher'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Voucher Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedVoucher?.voucher_type === 'payment' ? 
                <ArrowUp className="w-5 h-5 text-red-400" /> :
                <ArrowDown className="w-5 h-5 text-green-400" />
              }
              {selectedVoucher?.voucher_number}
              <Badge variant="outline" className={STATUS_COLORS[selectedVoucher?.status]}>
                {selectedVoucher?.status}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          {selectedVoucher && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Date: </span>
                  <span className="font-medium">{formatDate(selectedVoucher.date)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Payment Method: </span>
                  <span className="font-medium">{PAYMENT_METHODS.find(m => m.value === selectedVoucher.payment_method)?.label}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{selectedVoucher.voucher_type === 'payment' ? 'Payee' : 'Received From'}: </span>
                  <span className="font-medium">{selectedVoucher.payee_name || selectedVoucher.received_from}</span>
                </div>
                {selectedVoucher.cheque_number && (
                  <div>
                    <span className="text-muted-foreground">Cheque #: </span>
                    <span className="font-medium">{selectedVoucher.cheque_number}</span>
                  </div>
                )}
                {selectedVoucher.reference && (
                  <div>
                    <span className="text-muted-foreground">Reference: </span>
                    <span className="font-medium">{selectedVoucher.reference}</span>
                  </div>
                )}
                {selectedVoucher.manual_voucher_reference && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Manual / External Voucher #: </span>
                    <span className="font-medium text-amber-600 dark:text-amber-400">
                      {selectedVoucher.manual_voucher_reference}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3">Description</th>
                      <th className="text-right p-3">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedVoucher.lines?.map((line, idx) => (
                      <tr key={idx} className="border-t border-border">
                        <td className="p-3">{line.description}</td>
                        <td className="p-3 text-right font-medium">{formatCurrency(line.amount)}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-border bg-muted/30">
                      <td className="p-3 font-bold">Total</td>
                      <td className={`p-3 text-right font-bold text-lg ${selectedVoucher.voucher_type === 'payment' ? 'text-red-400' : 'text-green-400'}`}>
                        {formatCurrency(selectedVoucher.total_amount)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {selectedVoucher.narration && (
                <div>
                  <span className="text-sm text-muted-foreground">Narration: </span>
                  <p className="text-sm">{selectedVoucher.narration}</p>
                </div>
              )}
              
              <div className="text-xs text-muted-foreground">
                Created by {selectedVoucher.created_by_name} on {formatDate(selectedVoucher.created_at)}
                {selectedVoucher.approved_by_name && (
                  <> • Approved by {selectedVoucher.approved_by_name} on {formatDate(selectedVoucher.approved_at)}</>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            {selectedVoucher?.status === 'pending' && isAdmin && (
              <>
                <Button 
                  variant="outline" 
                  className="border-red-500 text-red-500 hover:bg-red-500/10"
                  onClick={() => handleApprove(selectedVoucher, 'reject')}
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button 
                  className="bg-green-500 hover:bg-green-600"
                  onClick={() => handleApprove(selectedVoucher, 'approve')}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowViewModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voucher PDF Preview */}
      <Dialog open={pdfPreviewOpen} onOpenChange={(open) => {
        setPdfPreviewOpen(open);
        if (!open && pdfPreviewUrl) {
          window.URL.revokeObjectURL(pdfPreviewUrl);
          setPdfPreviewUrl('');
        }
      }}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-4xl max-h-[92vh] overflow-hidden p-0">
          <DialogHeader className="px-4 sm:px-6 pt-4">
            <DialogTitle>{pdfPreviewTitle || 'Voucher PDF'}</DialogTitle>
            <DialogDescription>Preview the branded voucher before printing or sharing.</DialogDescription>
          </DialogHeader>
          <div className="px-4 sm:px-6 pb-2">
            {pdfPreviewUrl ? (
              <iframe
                src={pdfPreviewUrl}
                title="Voucher PDF Preview"
                className="w-full h-[70vh] border rounded bg-white"
                data-testid="voucher-pdf-iframe"
              />
            ) : (
              <div className="flex items-center justify-center h-[60vh] text-muted-foreground">Loading…</div>
            )}
          </div>
          <DialogFooter className="px-4 sm:px-6 pb-4 gap-2">
            {pdfPreviewUrl && (
              <>
                <Button variant="outline" onClick={() => window.open(pdfPreviewUrl, '_blank')} data-testid="voucher-pdf-open-tab">
                  Open in New Tab
                </Button>
                <Button onClick={() => {
                  const a = document.createElement('a');
                  a.href = pdfPreviewUrl;
                  a.download = `${pdfPreviewTitle || 'voucher'}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }} data-testid="voucher-pdf-download">
                  Download
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={() => setPdfPreviewOpen(false)} data-testid="voucher-pdf-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Attach Receipt to Invoice dialog ===== */}
      <Dialog open={attachDialogOpen} onOpenChange={setAttachDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="attach-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Attach receipt to invoice</DialogTitle>
            <DialogDescription>
              {attachVoucher ? (
                <>
                  Link <span className="font-semibold">{attachVoucher.voucher_number}</span>
                  {' '}(amount <span className="font-semibold">{Number(attachVoucher.total_amount || 0).toLocaleString()}</span>)
                  to an outstanding invoice. Picking the invoice will auto-correct the payer to the invoice&apos;s customer and update Statement of Accounts + balance immediately.
                </>
              ) : 'Pick an invoice to link this receipt to.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={attachInvoiceQuery}
                onChange={(e) => setAttachInvoiceQuery(e.target.value)}
                placeholder="Search by invoice number or customer name…"
                className="pl-9"
                data-testid="attach-invoice-search"
                autoFocus
              />
            </div>

            <div className="max-h-[380px] overflow-y-auto border rounded-lg divide-y">
              {attachLoading ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  <CircleNotch className="animate-spin mx-auto mb-2" size={20} />
                  Loading outstanding invoices…
                </div>
              ) : attachInvoiceMatches.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  No outstanding invoices match. Try clearing the search or check that the invoice is Approved (not Draft/Proforma).
                </div>
              ) : (
                attachInvoiceMatches.map((inv) => (
                  <button
                    key={inv.id}
                    onClick={() => handleConfirmAttach(inv)}
                    disabled={attachSubmitting}
                    data-testid={`attach-invoice-pick-${inv.document_number}`}
                    className="w-full text-left px-4 py-2.5 hover:bg-accent transition flex items-center justify-between gap-3 disabled:opacity-60"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{inv.document_number}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {inv.customer_name} · {inv.date}
                        {inv.invoice_type && inv.invoice_type !== 'invoice' ? ` · ${inv.invoice_type}` : ''}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-muted-foreground">Total</div>
                      <div className="font-semibold text-sm">{Number(inv.total || 0).toLocaleString()}</div>
                      {inv.balance_due !== undefined && (
                        <div className="text-[11px] text-rose-500">
                          Bal: {Number(inv.balance_due || 0).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAttachDialogOpen(false)} disabled={attachSubmitting} data-testid="attach-invoice-cancel">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
